import { FC, ReactNode, useState } from "react";
import {
	Box,
	Button,
	Divider,
	InputBase,
	ListSubheader,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import {
	EffectiveProgramFilters,
	groupOrgUnitsByProgram,
	programContextLine,
	UserProgramRef,
} from "../../../../utils/programSelection";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import HubIcon from "@mui/icons-material/Hub";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import { Program } from "../../../../domain/program.interface";

/** Props shared by every Reports Center tab — program is selected per report. */
export interface ReportTabProps {
	/** First selected program. The single-program reports still read this. */
	programId: string;
	/**
	 * The full selection (SIMS-7990). Program Activity Summary and Visit Detail —
	 * the reports this Epic feeds — read this instead of `programId`.
	 */
	programIds?: string[];
	programs: Program[];
	/**
	 * SIMS-9293: true until the caller's assigned programs are known. While it is,
	 * `programs` is an empty list that means "not loaded yet", NOT "assigned none" —
	 * so the no-programs message and the Run action both wait for it.
	 */
	programsLoading?: boolean;
	onProgramChange: (id: string) => void;
	onProgramsChange?: (ids: string[]) => void;
}

export const programNameOf = (
	programs: Program[],
	id: string,
): string | undefined => programs.find((p) => p._id === id)?.name;

/**
 * SIMS-7396: the payers configured for a program (`program.payers`) intersected
 * with the account's active payers. Returns [] when the program has no payers
 * configured — callers disable the Payer filter in that case.
 */
export const payersForProgram = <T extends { _id: string }>(
	activePayers: readonly T[] | undefined,
	program: Pick<Program, "payers"> | undefined,
): T[] => {
	const ids = program?.payers ?? [];
	if (ids.length === 0) return [];
	return (activePayers ?? []).filter((p) => ids.includes(p._id));
};

/**
 * SIMS-7994 AC3: the payers configured across ALL selected programs, de-duplicated.
 *
 * The multi-program reports were calling `payersForProgram` with
 * `programs.find((p) => p._id === programId)` — the FIRST selected program only. That
 * made the Payer filter a property of whichever program happened to sort first, so
 * selecting a program that has payers alongside one that does not left the filter
 * disabled. AC3 is explicit that the union governs: "when at least one does, it is
 * enabled". Caught by TestRail C15460, which failed on both Program Activity Summary
 * and Visit Detail.
 *
 * Union, not intersection: a payer configured on any selected program is a legitimate
 * filter value for the combined report — the same rule the org-unit options already
 * follow via `combinedOrgUnitIds`.
 */
export const payersForPrograms = <T extends { _id: string }>(
	activePayers: readonly T[] | undefined,
	programs: ReadonlyArray<Pick<Program, "payers">>,
): T[] => {
	const ids = new Set(programs.flatMap((program) => program?.payers ?? []));
	if (ids.size === 0) return [];
	return (activePayers ?? []).filter((p) => ids.has(p._id));
};

/**
 * SIMS-7397: the org-unit ids the user has for a given program (from
 * userInfo.programs) — the OU scope the Beneficiary Report uses.
 */
export const programOrgUnitIds = (
	userPrograms:
		| ReadonlyArray<{
				programId: string;
				orgUnitId?: ReadonlyArray<string | { orgUnitId: string }>;
		  }>
		| undefined,
	programId: string,
): string[] =>
	(userPrograms ?? [])
		.filter((p) => p.programId === programId)
		// SIMS-7397: orgUnitId entries can be either a plain id string or an
		// { orgUnitId } object at runtime — handle both, mirroring the Beneficiary
		// Report. (Assuming only the object shape returned undefined ids, which
		// left the Group Labels list empty.)
		.flatMap((p) =>
			(p.orgUnitId ?? []).map((o) => (typeof o === "string" ? o : o.orgUnitId)),
		)
		.filter((id): id is string => Boolean(id));

/**
 * SIMS-7397: the group labels attached to the given org-unit sites. Used to
 * scope the Group Labels filter to the selected program's OUs, mirroring the
 * Group Labels logic in the Beneficiary Report.
 */
export const groupLabelsForSites = <T extends { _id: string }>(
	groupLabels: readonly T[] | undefined,
	sites: ReadonlyArray<{ groupLabelIds?: string[] }> | undefined,
): T[] => {
	const ids = (sites ?? []).flatMap((s) => s.groupLabelIds ?? []);
	return (groupLabels ?? []).filter((l) => ids.includes(l._id));
};

/** Uppercase label-above-control field used across the report filter bars. */
export const FilterField: FC<{
	label: string;
	required?: boolean;
	badge?: string;
	/** Muted line under the control (e.g. "N of M assigned programs selected"). */
	helperText?: string;
	/**
	 * Span every column of the filter grid. The program chip group shows all
	 * assigned programs at once, so it needs the full row — the other filters go
	 * on the next one.
	 */
	fullRow?: boolean;
	/**
	 * Start a fresh grid row. Pinning the item to column 1 makes the grid wrap, which
	 * is how the date filters get a row of their own instead of trailing the others.
	 */
	newRow?: boolean;
	labelColor: string;
	errorColor?: string;
	children: ReactNode;
}> = ({
	label,
	required,
	badge,
	helperText,
	fullRow,
	newRow,
	labelColor,
	errorColor,
	children,
}) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			gap: 1,
			minWidth: 0,
			...(fullRow ? { gridColumn: "1 / -1" } : {}),
			...(newRow && !fullRow ? { gridColumn: { md: "1" } } : {}),
		}}
	>
		<Stack direction="row" spacing={1} alignItems="center">
			<Typography
				sx={{
					fontSize: 12,
					fontWeight: 600,
					letterSpacing: "0.06em",
					color: labelColor,
				}}
			>
				{label.toUpperCase()}
				{required && (
					<Box component="span" sx={{ color: errorColor, ml: 0.5 }}>
						*
					</Box>
				)}
			</Typography>
			{badge && (
				<Box
					component="span"
					sx={{
						bgcolor: "primary.light",
						color: "primary.main",
						fontSize: 10,
						fontWeight: 700,
						letterSpacing: "0.04em",
						px: 1,
						py: 0.25,
						borderRadius: "9999px",
					}}
				>
					{badge}
				</Box>
			)}
		</Stack>
		{children}
		{/*
		 * Always rendered, even when empty. Only some filters carry a sub-label, and
		 * letting it change the field's height made the controls in a row stop lining
		 * up — reserving the line keeps every field the same height.
		 */}
		<Typography sx={{ fontSize: 12, color: labelColor, minHeight: 18 }}>
			{helperText ?? ""}
		</Typography>
	</Box>
);

/** The Program selector rendered as the first filter in every report. */
/**
 * The Program filter.
 *
 * Single-select by default. Pass `programIds` + `onProgramsChange` to get the
 * **multi-program** control, rendered as a multi-select dropdown.
 *
 * Multi is opt-in because this field is shared across reports and the single-select
 * path must keep working for any consumer that does not pass `onProgramsChange`.
 * **Exactly three** ReportsDashboard tabs pass it — Program Activity Summary, Visit
 * Detail and Disbursed Incentives. **Enrollment Summary does NOT**: it renders the
 * single-select branch below and never had the multi-program control. (An earlier
 * revision of this comment claimed Enrollment Summary was multi-program. It never
 * was; the claim was wrong when written and is corrected here, as the SIMS-9290
 * decision session asked. Enrollment Summary is explicitly out of scope for that
 * Epic — SIMS-9292's Definition of Done requires it confirmed unchanged.)
 *
 * **SIMS-9292 (decision D4) — an empty selection means EVERY assigned program.**
 * Reaching an empty selection is ordinary and is not blocked, auto-restored or
 * prompted: the closed box reads "All programs" and the caller resolves it to the
 * assigned ids via `resolveProgramSelection`. The chip control, the `MULTI` badge,
 * the required marker and the "At least one required" helper went with that
 * decision. The control now mirrors `OrgUnitFilterField` beside it — a closed box
 * opening a searchable checkbox list — because the two sat on the same filter bar
 * with two different interaction models, which is the problem the Epic exists to fix.
 *
 * The Org-units panel's grouping (its "Shared across programs" header and "in N
 * programs" count) is deliberately NOT mirrored: an org unit can belong to several
 * programs, while a program belongs to nothing above it. Program renders one flat
 * list.
 */
export const ProgramFilterField: FC<{
	programId?: string;
	programIds?: string[];
	programs: Program[];
	onProgramChange?: (id: string) => void;
	onProgramsChange?: (ids: string[]) => void;
	labelColor: string;
	mutedColor: string;
	errorColor: string;
}> = ({
	programId,
	programIds,
	programs,
	onProgramChange,
	onProgramsChange,
	labelColor,
	mutedColor,
	errorColor,
}) => {
	const multi = Boolean(onProgramsChange);
	const selected = programIds ?? [];
	const [query, setQuery] = useState("");

	if (multi) {
		/**
		 * AC1 / AC4, portal Q-11 = A: the closed box reads "All programs" when the
		 * selection is EMPTY — the identical rule `OrgUnitFilterField` below uses for
		 * "All org units", which is what AC4's "both are the same kind of control"
		 * means at the level a user can see.
		 *
		 * Under D4 an empty selection already means every assigned program, so this
		 * is a faithful label and not a placeholder: `resolveProgramSelection`
		 * expands it to the assigned ids and sends them explicitly.
		 *
		 * The dashboard no longer pre-selects every assigned id on entry for these
		 * three reports (index.tsx, SIMS-9292 AC1) — that SIMS-7990 default is what
		 * made an untouched filter read "8 selected", and widening the label to
		 * "covers everything assigned" only hid it: with every row arriving ticked,
		 * ticking two programs read "6 selected" because the clicks UNticked. Fixing
		 * the default is what AC2 actually requires; this condition stays narrow.
		 */
		const coversEveryProgram = selected.length === 0;

		const matches = (p: Program): boolean =>
			!query ||
			(p.name ?? "").toLowerCase().includes(query.trim().toLowerCase());
		const toggle = (id: string): void =>
			onProgramsChange?.(
				selected.includes(id)
					? selected.filter((s) => s !== id)
					: [...selected, id],
			);

		return (
			<FilterField
				label="Program"
				labelColor={labelColor}
				errorColor={errorColor}
				// No `required`, no `MULTI` badge and no `fullRow`: under D4 a program
				// always applies, so Program is an ordinary filter occupying one cell
				// like the four beside it.
				helperText={
					coversEveryProgram
						? "Every program you are assigned to"
						: `${selected.length} of ${programs.length} assigned programs`
				}
			>
				<Select
					multiple
					size="small"
					displayEmpty
					data-testid="program-filter"
					value={selected}
					input={<OutlinedInput />}
					// The rows own the toggling, so the Select's own change is a no-op —
					// the same arrangement OrgUnitFilterField uses.
					onChange={() => undefined}
					renderValue={() =>
						coversEveryProgram ? (
							// AC1/AC3: "all" is stated in the same words and the same muted
							// styling as "All org units" beside it.
							<Typography sx={{ color: mutedColor }}>All programs</Typography>
						) : (
							<Typography sx={{ fontSize: 14 }}>
								{selected.length} selected
							</Typography>
						)
					}
					MenuProps={{ PaperProps: { sx: { width: 420, maxHeight: 460 } } }}
				>
					<Box
						sx={{ px: 2.25, py: 1.75 }}
						// Keystrokes typed into the search box must not reach the Select,
						// which would treat them as type-ahead and jump the highlight
						// between programs while the user is still typing.
						//
						// Escape and Tab are deliberately let through (SIMS-9292). The
						// search box is autoFocused, so swallowing EVERY key meant Escape
						// never reached the menu and the panel could not be dismissed from
						// the keyboard at all — it had to be closed by clicking away. Those
						// two keys are the dismissal path, not type-ahead, so they are not
						// the ones this guard is for.
						onKeyDown={(e) => {
							if (e.key === "Escape" || e.key === "Tab") return;
							// ArrowDown leaves the search box for the first program row;
							// from there MUI's own list navigation takes over. Without it
							// a keyboard-only user could open the panel but never reach a
							// row: the arrows were stopped here and Tab closes the menu.
							if (e.key === "ArrowDown") {
								e.preventDefault();
								e.stopPropagation();
								e.currentTarget.parentElement
									?.querySelector<HTMLElement>('li[role="option"]')
									?.focus();
								return;
							}
							e.stopPropagation();
						}}
					>
						<InputBase
							fullWidth
							autoFocus
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search programs"
							startAdornment={
								<SearchIcon
									sx={{ fontSize: 20, color: mutedColor, mr: 1.25 }}
								/>
							}
							sx={{ fontSize: 14 }}
						/>
					</Box>
					<Divider />

					{programs.filter(matches).map((p) => (
						<MenuItem
							key={p._id}
							value={p._id}
							onClick={() => toggle(p._id)}
							// `value` is what makes the row announce its state. MUI's Select
							// computes each option's `aria-selected` by matching the child's
							// `value` against the Select's, and OVERRIDES a `selected` prop
							// passed in by hand — so without a value every row reports
							// aria-selected="false" and the listbox reads as "nothing
							// selected" to a screen reader however many programs are ticked,
							// the state being carried by the checkbox glyph alone.
							//
							// The Select's own onChange stays a no-op; `toggle` above remains
							// the only thing that mutates the selection.
							sx={{
								height: 44,
								px: 2.25,
								gap: 1.5,
								// `selected` is carried for the ARIA state ONLY. MUI also
								// paints a tinted background for it, which the Org units rows
								// beside this control do not have — and AC4 is that the two
								// read as the same control. The checkbox stays the single
								// visual cue; hover keeps the default so the row still
								// responds to the pointer.
								"&.Mui-selected": { backgroundColor: "transparent" },
								"&.Mui-selected:hover": { backgroundColor: "action.hover" },
							}}
						>
							{selected.includes(p._id) ? (
								<CheckBoxIcon sx={{ fontSize: 20, color: "primary.main" }} />
							) : (
								<CheckBoxOutlineBlankIcon
									sx={{ fontSize: 20, color: mutedColor }}
								/>
							)}
							<Typography sx={{ fontSize: 14 }}>{p.name}</Typography>
						</MenuItem>
					))}
				</Select>
			</FilterField>
		);
	}

	return (
		<FilterField
			label="Program"
			required
			labelColor={labelColor}
			errorColor={errorColor}
		>
			<Select
				size="small"
				displayEmpty
				value={programId ?? ""}
				input={<OutlinedInput />}
				onChange={(e) => onProgramChange?.(e.target.value)}
				renderValue={(v) => {
					// Single-select path: a program is always selected — no
					// empty/placeholder option. The muted fallback only shows when the
					// user has no assigned programs at all.
					const name = programs.find((p) => p._id === v)?.name;
					return (
						name ?? (
							<Typography sx={{ color: mutedColor }}>No programs</Typography>
						)
					);
				}}
			>
				{programs.map((p) => (
					<MenuItem key={p._id} value={p._id}>
						{p.name}
					</MenuItem>
				))}
			</Select>
		</FilterField>
	);
};

/**
 * Org-unit filter, grouped the way the hand-off's "Org unit dropdown — grouped by
 * program, shared group first" panel shows it (SIMS-7994 AC2 / FR-4).
 *
 * Org units in more than one selected program come first under "Shared across
 * programs", tinted and carrying an "in N programs" count; the rest are listed under
 * their own program. Grouping semantics live in `groupOrgUnitsByProgram` so they are
 * unit-tested independently of this markup.
 */
export const OrgUnitFilterField: FC<{
	userPrograms: UserProgramRef[] | undefined | null;
	selectedProgramIds: string[];
	programs: Program[];
	/** All selectable org units, for resolving names. */
	options: ReadonlyArray<{ id: string; name?: string | null }>;
	value: string[];
	onChange: (ids: string[]) => void;
	labelColor: string;
	mutedColor: string;
	disabled?: boolean;
}> = ({
	userPrograms,
	selectedProgramIds,
	programs,
	options,
	value,
	onChange,
	labelColor,
	mutedColor,
	disabled = false,
}) => {
	const [query, setQuery] = useState("");
	const { shared, byProgram } = groupOrgUnitsByProgram(
		userPrograms,
		selectedProgramIds,
	);

	const nameOf = (id: string): string =>
		options.find((o) => o.id === id)?.name ?? "";
	const programNameFor = (id: string): string =>
		programs.find((p) => p._id === id)?.name ?? "";
	/**
	 * Only offer org units the options list actually named.
	 *
	 * The grouping comes from the caller's entitlements (`userInfo.programs`), which
	 * can reference org units this report's options request did not return. Falling
	 * back to the raw id rendered a bare ObjectId in the list — worse than useless,
	 * since it is neither recognisable nor searchable. Hide those instead.
	 */
	const known = (id: string): boolean => Boolean(nameOf(id));
	const matches = (id: string): boolean =>
		known(id) &&
		(!query || nameOf(id).toLowerCase().includes(query.trim().toLowerCase()));

	const toggle = (id: string): void =>
		onChange(
			value.includes(id) ? value.filter((v) => v !== id) : [...value, id],
		);

	const row = (id: string, count?: number): ReactNode => (
		<MenuItem
			key={id}
			onClick={() => toggle(id)}
			sx={{ height: 44, px: 2.25, gap: 1.5 }}
		>
			{value.includes(id) ? (
				<CheckBoxIcon sx={{ fontSize: 20, color: "primary.main" }} />
			) : (
				<CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: mutedColor }} />
			)}
			<Typography sx={{ fontSize: 14 }}>{nameOf(id)}</Typography>
			{count !== undefined && (
				<Typography sx={{ ml: "auto", fontSize: 12, color: mutedColor }}>
					in {count} programs
				</Typography>
			)}
		</MenuItem>
	);

	const groupHeader = (label: string, isShared: boolean): ReactNode => (
		<ListSubheader
			key={`h-${label}`}
			sx={{
				px: 2.25,
				pt: isShared ? 1.25 : 1.5,
				pb: 0.75,
				lineHeight: 1.4,
				display: "flex",
				alignItems: "center",
				gap: 1,
				fontSize: 11,
				fontWeight: 500,
				letterSpacing: "0.08em",
				textTransform: "uppercase",
				color: isShared ? "primary.main" : mutedColor,
				bgcolor: isShared ? "primary.lighter" : "background.paper",
			}}
		>
			{isShared && <HubIcon sx={{ fontSize: 16, color: "primary.main" }} />}
			{label}
		</ListSubheader>
	);

	return (
		<FilterField
			label="Org units"
			labelColor={labelColor}
			// Sub-label from the hand-off. Surfacing the shared count here means the
			// grouping is visible without opening the panel.
			helperText={
				shared.length > 0
					? `Grouped by program · ${shared.length} shared`
					: "Grouped by program"
			}
		>
			<Select
				multiple
				size="small"
				displayEmpty
				data-testid="org-unit-filter"
				disabled={disabled}
				value={value}
				input={<OutlinedInput />}
				// The rows own the toggling, so the Select's own change is a no-op.
				onChange={() => undefined}
				renderValue={(sel) =>
					(sel as string[]).length === 0 ? (
						<Typography sx={{ color: mutedColor }}>All org units</Typography>
					) : (
						<Typography sx={{ fontSize: 14 }}>
							{(sel as string[]).length} selected
						</Typography>
					)
				}
				MenuProps={{ PaperProps: { sx: { width: 420, maxHeight: 460 } } }}
			>
				<Box sx={{ px: 2.25, py: 1.75 }} onKeyDown={(e) => e.stopPropagation()}>
					<InputBase
						fullWidth
						autoFocus
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Search org units"
						startAdornment={
							<SearchIcon sx={{ fontSize: 20, color: mutedColor, mr: 1.25 }} />
						}
						sx={{ fontSize: 14 }}
					/>
				</Box>
				<Divider />

				{shared.filter((s) => matches(s.orgUnitId)).length > 0 &&
					groupHeader("Shared across programs", true)}
				{shared
					.filter((s) => matches(s.orgUnitId))
					.map((s) => row(s.orgUnitId, s.programCount))}

				{byProgram.map((group) => {
					const visible = group.orgUnitIds.filter(matches);
					const programName = programNameFor(group.programId);
					// Skip the group when nothing in it is showable, or when the program
					// itself has no name to head it with.
					if (visible.length === 0 || !programName) return null;
					return [
						groupHeader(programName, false),
						...visible.map((id) => row(id)),
					];
				})}

				<Divider />
				<Typography
					sx={{
						px: 2.25,
						py: 1.5,
						fontSize: 12,
						lineHeight: "18px",
						color: mutedColor,
					}}
				>
					A shared org unit applies to every selected program that includes it.
				</Typography>
			</Select>
		</FilterField>
	);
};

/**
 * Filter-bar actions — "Clear all" and "Run report" (SIMS-8023, FR-15/16).
 *
 * Sits in the last cell of the filter grid, right-aligned, per the hand-off.
 *
 * `Run report` is the ONLY way results are generated: selecting filters must not
 * execute anything. It is disabled while no program is selected, which is the same
 * guard as the "Select a program" prompt.
 */
export const ReportActions: FC<{
	onClearAll: () => void;
	onRun: () => void;
	canRun: boolean;
}> = ({ onClearAll, onRun, canRun }) => (
	/*
	 * TWO grid cells, not one. The filter grid's last column is 112px — both buttons
	 * in a single cell were cramped and wrapped. Returning a fragment lets the grid
	 * auto-place them: Clear all takes the free cell before the last, Run report the
	 * last one. Both align to the bottom so they line up with the filter boxes.
	 */
	<>
		<Box
			sx={{
				// Auto-placed, like Enrollment Summary. Pinning them to fixed columns
				// forced Run report into the old 112px trailing column, which is what
				// made these two reports look unaligned; that column is now gone.
				display: "flex",
				alignItems: "flex-end",
				justifyContent: "flex-end",
				height: 48,
			}}
		>
			<Button
				variant="text"
				onClick={onClearAll}
				sx={{
					height: 48,
					fontSize: 14,
					textTransform: "none",
					color: "primary.main",
					whiteSpace: "nowrap",
				}}
			>
				Clear all
			</Button>
		</Box>
		<Box sx={{ display: "flex", alignItems: "flex-end", height: 48 }}>
			<Button
				fullWidth
				variant="contained"
				onClick={onRun}
				disabled={!canRun}
				startIcon={<PlayArrowIcon sx={{ fontSize: 20 }} />}
				sx={{
					height: 48,
					px: 2.5,
					borderRadius: 2,
					fontSize: 14,
					fontWeight: 500,
					letterSpacing: "0.04em",
					whiteSpace: "nowrap",
				}}
			>
				Run report
			</Button>
		</Box>
	</>
);

/**
 * The exclusion banner's sentence, as text (SIMS-8006 AC1).
 *
 * Extracted from the component so the PDF export can state the same exclusion in the
 * same words (SIMS-8062 / NFR-4). A structured PDF cannot screenshot the banner, and a
 * report that silently drops the notice would over-claim its own coverage — the reader
 * would have no way to tell that programs were left out.
 */
export const exclusionBannerText = (
	hiddenCount: number,
	selectedCount: number,
): string =>
	`${hiddenCount} of ${selectedCount} selected programs are not shown. They have no data you have access to for the filters applied.`;

/**
 * Banner for programs excluded from the results (SIMS-8006 AC1, FR-5).
 *
 * Deliberately cause-NEUTRAL: it covers both "no data after filtering" and "nothing
 * you are entitled to" without distinguishing them, because saying which would
 * itself disclose whether data exists for a program the user cannot see. The
 * hand-off's wording does exactly that, and it is reproduced verbatim.
 *
 * Renders nothing when no program was excluded.
 */
export const ExclusionBanner: FC<{
	hiddenCount: number;
	selectedCount: number;
	onViewPrograms?: () => void;
}> = ({ hiddenCount, selectedCount, onViewPrograms }) => {
	if (hiddenCount <= 0) return null;
	return (
		<Box
			sx={{
				bgcolor: "primary.light",
				borderLeft: 4,
				borderColor: "primary.main",
				borderRadius: 2,
				px: 2.5,
				py: 1.75,
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				mb: 2.5,
			}}
		>
			<InfoOutlinedIcon sx={{ fontSize: 22, color: "primary.main" }} />
			<Typography sx={{ fontSize: 14, lineHeight: "22px" }}>
				{exclusionBannerText(hiddenCount, selectedCount)}
			</Typography>
			{onViewPrograms && (
				<Button
					variant="text"
					onClick={onViewPrograms}
					sx={{
						ml: "auto",
						fontSize: 13,
						fontWeight: 500,
						letterSpacing: "0.04em",
						color: "primary.main",
						flexShrink: 0,
					}}
				>
					View programs
				</Button>
			)}
		</Box>
	);
};

/**
 * Per-program context lines — the filters actually applied to each selected
 * program (SIMS-8014 AC1, FR-6).
 *
 * Driven entirely by the response's `programFilters`, which the backend projects
 * from the same per-(program, org-unit) scopes it built the query's `$match` from.
 * That is the point of FR-6: the line is an account of what the query really did,
 * not a re-derivation from the client's copy of the entitlements, which could
 * disagree with the rows on screen.
 *
 * Excluded programs are skipped — they contribute no rows, and the
 * `ExclusionBanner` already reports them as a cause-neutral count. Renders nothing
 * when the server sent no `programFilters` (an older API), rather than an empty
 * heading.
 *
 * Scope note: this is the context LINE only. Per-program KPI sections are FR-7
 * (SIMS-7988) and the Visit Detail Program column is FR-12 (SIMS-7989).
 */
export const ProgramContextLines: FC<{
	programFilters?: EffectiveProgramFilters[];
	programs: Program[];
	orgUnitOptions: Array<{ id: string; name: string | null }>;
	borderColor: string;
	mutedColor: string;
}> = ({
	programFilters,
	programs,
	orgUnitOptions,
	borderColor,
	mutedColor,
}) => {
	const shown = (programFilters ?? []).filter((program) => !program.excluded);
	if (shown.length === 0) return null;

	// Falls back to the id when the option carries no name (the option type allows
	// null) or is missing from the list. A blank would read as an empty filter
	// value, which is more misleading than an unresolved id.
	const orgUnitNameOf = (orgUnitId: string): string =>
		orgUnitOptions.find((option) => option.id === orgUnitId)?.name ?? orgUnitId;
	const programNameOf = (programId: string): string =>
		programs.find((program) => program._id === programId)?.name ?? programId;

	return (
		<Paper
			variant="outlined"
			sx={{ borderColor, borderRadius: 3, px: 2.5, py: 2, mb: 2.5 }}
		>
			<Typography sx={{ fontSize: 13, color: mutedColor, mb: 1 }}>
				Filters applied per program
			</Typography>
			<Stack spacing={1.25}>
				{shown.map((program) => {
					const line = programContextLine(program, orgUnitNameOf);
					return (
						<Box key={program.programId}>
							<Typography sx={{ fontSize: 14, fontWeight: 600 }}>
								{programNameOf(program.programId)}
								{!program.unrestricted && (
									<Typography
										component="span"
										sx={{ fontSize: 13, fontWeight: 400, color: mutedColor }}
									>
										{" · "}
										{program.orgUnitCount}{" "}
										{program.orgUnitCount === 1 ? "org unit" : "org units"}
									</Typography>
								)}
							</Typography>
							{line && (
								<Typography sx={{ fontSize: 13, color: mutedColor }}>
									{line}
								</Typography>
							)}
						</Box>
					);
				})}
			</Stack>
		</Paper>
	);
};

/**
 * The empty result state, in one of two variants.
 *
 * `"no-selection"` (default) — "Select a program to run the report." This is the
 * ORIGINAL card and it is still correct for the **single-select** Enrollment Summary
 * report, where one program genuinely is required. Enrollment Summary is explicitly
 * out of scope for SIMS-9290 (decision D6) and SIMS-9292's Definition of Done
 * requires it confirmed unchanged, which is why this wording is preserved rather
 * than replaced.
 *
 * `"no-assigned-programs"` (SIMS-9293) — "You have no programs assigned. Contact
 * your administrator." On the three multi-program reports an empty SELECTION now
 * means every assigned program (D4), so the only way to see nothing is to be
 * assigned nothing. The variant is chosen on the ASSIGNMENT, never on the selection:
 * guarding it on the selection would show "you have no programs assigned" to every
 * user who simply unticks everything.
 *
 * It is a message, not an access block — D5 considered blocking the Reports Center
 * and rejected it as more work with a risk of breaking access that works today.
 */
export const NoProgramCard: FC<{
	borderColor: string;
	mutedColor: string;
	variant?: "no-selection" | "no-assigned-programs";
}> = ({ borderColor, mutedColor, variant = "no-selection" }) => {
	const noPrograms = variant === "no-assigned-programs";
	return (
		<Paper
			variant="outlined"
			data-testid="no-programs-card"
			sx={{ borderColor, borderRadius: 3, py: 9, px: 3, textAlign: "center" }}
		>
			<FilterAltOutlinedIcon sx={{ fontSize: 48, color: mutedColor }} />
			<Typography sx={{ fontSize: 20, mt: 1 }}>
				{noPrograms
					? "You have no programs assigned. Contact your administrator."
					: "Select a program to run the report."}
			</Typography>
			{!noPrograms && (
				// Dropped for the no-programs variant: under D4 "a program is required"
				// is no longer true on the three multi-program reports, and repeating it
				// would contradict the message above it.
				<Typography sx={{ fontSize: 15, color: mutedColor, mt: 0.5 }}>
					A program is required before any report data can be shown.
				</Typography>
			)}
		</Paper>
	);
};
